import { useContext, useEffect, useRef, useState } from "react"

import { AppContext } from "../App"
import { LetterStatus } from "../helpers"

type Props = {
  letterPos: number
  attemptVal: number
}

function Letter({ letterPos, attemptVal }: Props) {
  const {
    board,
    boardStatus,
    currAttempt,
    letterStatus,
    setLetterStatus,
    revealingRow,
    revealingBoardStatus,
  } = useContext(AppContext)
  const letter = board[attemptVal][letterPos]
  const tileRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const prevLetterRef = useRef<string>("")
  // Initialize flipped state based on whether this row is already completed
  const [isFlipped, setIsFlipped] = useState(currAttempt.attempt > attemptVal)

  // Determine if this letter should show its final state (flipped to back)
  const shouldShowFinalState = currAttempt.attempt > attemptVal || isFlipped

  // Get the back color class - this should always be determined if we have status info
  let backColorClass = ""
  let finalStatus = LetterStatus.Unknown

  // Priority 1: If this row is currently being revealed, use the revealing board status
  if (revealingRow === attemptVal && revealingBoardStatus) {
    finalStatus = revealingBoardStatus[attemptVal][letterPos]
  }
  // Priority 2: If this row is completed, use the regular board status
  else if (currAttempt.attempt > attemptVal) {
    finalStatus = boardStatus[attemptVal][letterPos]
  }

  // Convert status to color class for the back side
  switch (finalStatus) {
    case LetterStatus.Letter:
      backColorClass = "almost"
      break
    case LetterStatus.LetterAndPosition:
      backColorClass = "correct"
      break
    case LetterStatus.Disabled:
      backColorClass = "error"
      break
    default:
      // No color class means it will use the default grey from CSS
      break
  }

  // Add pulse when letter appears
  useEffect(() => {
    if (letter && letter !== prevLetterRef.current && currAttempt.attempt === attemptVal) {
      if (containerRef.current) {
        containerRef.current.classList.remove('pulse')
        // Force reflow to restart animation
        void containerRef.current.offsetHeight
        containerRef.current.classList.add('pulse')
        
        // Remove class after animation
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.classList.remove('pulse')
          }
        }, 200)
      }
    }
    prevLetterRef.current = letter
  }, [letter, currAttempt.attempt, attemptVal])

  // Handle reveal animation when word is submitted
  useEffect(() => {
    if (revealingRow === attemptVal) {
      // Increased delay: 350ms between each letter start + 100ms pause after each completes
      const delay = letterPos * 450 // Each letter starts 450ms after the previous
      
      setTimeout(() => {
        if (tileRef.current) {
          tileRef.current.classList.add('flipping')
          setIsFlipped(true)
        }
      }, delay)
    } else if (revealingRow !== attemptVal && revealingRow !== null) {
      // Reset state when revealing a different row
      if (tileRef.current) {
        tileRef.current.classList.remove('flipping')
      }
      setIsFlipped(false)
    }
  }, [revealingRow, attemptVal, letterPos])

  // Update flipped state when game state changes (e.g., on page load)
  useEffect(() => {
    const shouldBeFlipped = currAttempt.attempt > attemptVal
    if (shouldBeFlipped !== isFlipped && revealingRow !== attemptVal) {
      setIsFlipped(shouldBeFlipped)
      if (tileRef.current) {
        if (shouldBeFlipped) {
          tileRef.current.classList.add('flipping')
        } else {
          tileRef.current.classList.remove('flipping')
        }
      }
    }
  }, [currAttempt.attempt, attemptVal, isFlipped, revealingRow])

  // Update keyboard letter status
  useEffect(() => {
    if (currAttempt.attempt > attemptVal) {
      let newLetterStatus = new Map<string, LetterStatus>(letterStatus)
      const oldStatus = newLetterStatus.get(letter) || LetterStatus.Unknown
      const newStatus = boardStatus[attemptVal][letterPos]
      if (oldStatus < newStatus) {
        newLetterStatus.set(letter, newStatus)
        setLetterStatus(newLetterStatus)
      }
    }
  }, [
    currAttempt,
    letterStatus,
    attemptVal,
    boardStatus,
    letter,
    letterPos,
    setLetterStatus,
  ])

  return (
    <div ref={containerRef} className="letter-container">
      <div ref={tileRef} className={`letter-tile ${isFlipped ? 'flipping' : ''}`}>
        <div className="letter-front">
          {letter}
        </div>
        <div className={`letter-back ${backColorClass}`}>
          {letter}
        </div>
      </div>
    </div>
  )
}

export default Letter
